import React, { useEffect, useState } from "react";
import { DatePicker, Row, Col, Popover } from "antd";
import { CheckCircleTwoTone } from "@ant-design/icons";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { getProductPlanningResults } from "../../core/api/mediaplan";
import { useSelector } from "react-redux";
import Holidays from "date-holidays";
import ViewMediaPlan from "./components/viewMediaPlan";
const hd = new Holidays("US");

dayjs.extend(isoWeek);
const generateTimelineDates = (startDate, endDate) => {
  const timeline = [];
  let current = startDate.startOf("day");
  while (current.isBefore(endDate) || current.isSame(endDate, "day")) {
    const dateStr = current.format("YYYY-MM-DD");
    const holiday = hd.isHoliday(new Date(dateStr));
    timeline.push({
      key: current.format("YYYY-MM-DD"),
      month: current.format("MMMM-YYYY"),
      week: `Week ${String(current.isoWeek()).padStart(
        2,
        "0"
      )}-${current.isoWeekYear()}`,
      displayWeek: `Week ${String(current.isoWeek()).padStart(2, "0")}`,
      day: current.format("DD"),
      full: current,
      isSunday: current.day() === 0 || Boolean(holiday),
    });
    current = current.add(1, "day");
  }
  return timeline;
};

const TimelineTable = ({ setOpenViewTabs, setForcedTabKey, setActiveViewTabKey }) => {
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs().add(30, "day"));
  const [timeline, setTimeline] = useState([]);
  const [products, setProducts] = useState([]);
  const [openPopoverId, setOpenPopoverId] = useState(null);
  const [highlightedCell, setHighlightedCell] = useState({
    row: null,
    col: null,
  });
  const selectedClients = useSelector((state) => state.selectedClients);

  useEffect(() => {
    setTimeline(generateTimelineDates(startDate, endDate));
    const getProductPlanningData = async () => {
      try {
        const response = await getProductPlanningResults({
          client_ids: selectedClients,
          start_date: startDate.format("YYYY-MM-DD"),
          end_date: endDate.format("YYYY-MM-DD"),
        });
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching product planning data:", error);
      }
    };
    getProductPlanningData();
  }, [startDate, endDate, selectedClients]);

  const groupBy = (array, key) => {
    return array.reduce((result, item) => {
      const value = item[key];
      if (!result[value]) result[value] = [];
      result[value].push(item);
      return result;
    }, {});
  };

  const computeDailyTotals = () => {
    const dailyTotals = {}; // { date: { campaignQty: x, mailQty: y } }

    timeline.forEach((day) => {
      const dayKey = day.key;
      const campaignsSeen = new Set();
      let mailQtySum = 0;

      products.forEach((product) => {
        product.media_plans.forEach((plan) => {
          if (dayjs(plan.date).format("YYYY-MM-DD") === dayKey) {
            if (!campaignsSeen.has(plan.campaign_code)) {
              campaignsSeen.add(plan.campaign_code);
              mailQtySum += plan.target_mail_qty;
            }
          }
        });
      });

      dailyTotals[dayKey] = {
        campaignQty: campaignsSeen.size,
        mailQty: mailQtySum,
      };
    });

    return dailyTotals;
  };

  const dailyTotals = computeDailyTotals();

  const computeWeekTotals = () => {
    const weekTotals = {}; // { weekKey: { campaigns: {campaign_code: target_mail_qty} } }

    timeline.forEach((day) => {
      const weekKey = day.week;
      if (!weekTotals[weekKey]) {
        weekTotals[weekKey] = { campaigns: {} };
      }

      products.forEach((product) => {
        product.media_plans.forEach((plan) => {
          if (dayjs(plan.date).format("YYYY-MM-DD") === day.key) {
            if (!weekTotals[weekKey].campaigns[plan.campaign_code]) {
              weekTotals[weekKey].campaigns[plan.campaign_code] =
                plan.target_mail_qty;
            }
          }
        });
      });
    });

    const formattedWeekTotals = {};
    for (const key in weekTotals) {
      const campaigns = weekTotals[key].campaigns;
      formattedWeekTotals[key] = {
        campaignQty: Object.keys(campaigns).length,
        mailQty: Object.values(campaigns).reduce((sum, qty) => sum + qty, 0),
      };
    }

    return formattedWeekTotals;
  };

  const computeMonthTotals = () => {
    const monthTotals = {}; // { monthKey: { campaigns: {campaign_code: target_mail_qty} } }

    timeline.forEach((day) => {
      const monthKey = day.month;
      if (!monthTotals[monthKey]) {
        monthTotals[monthKey] = { campaigns: {} };
      }

      products.forEach((product) => {
        product.media_plans.forEach((plan) => {
          if (dayjs(plan.date).format("YYYY-MM-DD") === day.key) {
            if (!monthTotals[monthKey].campaigns[plan.campaign_code]) {
              monthTotals[monthKey].campaigns[plan.campaign_code] =
                plan.target_mail_qty;
            }
          }
        });
      });
    });

    const formattedMonthTotals = {};
    for (const key in monthTotals) {
      const campaigns = monthTotals[key].campaigns;
      formattedMonthTotals[key] = {
        campaignQty: Object.keys(campaigns).length,
        mailQty: Object.values(campaigns).reduce((sum, qty) => sum + qty, 0),
      };
    }

    return formattedMonthTotals;
  };

  const weekTotals = computeWeekTotals();
  const monthTotals = computeMonthTotals();

  const renderTableHeader = () => {
    const cells = (titles) =>
      titles.map((t, i) => (
        <th key={i} className={`text-center fw-semibold`}>
          {t}
        </th>
      ));

    const monthGroups = groupBy(timeline, "month");
    const weekGroups = groupBy(timeline, "week");
    const computeGrandTotals = () => {
      const seenCampaigns = new Set();
      const campaignMap = {}; // { campaign_code: target_mail_qty }

      products.forEach((product) => {
        product.media_plans.forEach((plan) => {
          if (!seenCampaigns.has(plan.campaign_code)) {
            seenCampaigns.add(plan.campaign_code);
            campaignMap[plan.campaign_code] = plan.target_mail_qty;
          }
        });
      });

      const totalCampaignQty = seenCampaigns.size;
      const totalMailQty = Object.values(campaignMap).reduce(
        (sum, qty) => sum + qty,
        0
      );

      return { totalCampaignQty, totalMailQty };
    };
    const { totalCampaignQty, totalMailQty } = computeGrandTotals();

    return (
      <thead className="table-light text-center align-middle">
        <tr>
          <th
            colSpan={3}
            className="bg-secondary text-white"
            style={{ zIndex: 3 }}
          >
            Month
          </th>
          {Object.entries(monthGroups).map(([month, days], i) => (
            <th
              key={"m" + i}
              colSpan={days.length}
              className="bg-secondary text-white"
            >
              {month}
            </th>
          ))}
        </tr>
        <tr>
          {cells(["Total Campaign Qty", totalCampaignQty, "Campaign Qty"])}
          {Object.entries(monthGroups).map(([month, days], i) => (
            <th key={"mcq" + i} colSpan={days.length} className="text-center">
              {monthTotals[month]?.campaignQty || 0}
            </th>
          ))}
        </tr>
        <tr>
          {cells(["Total Mail Qty", totalMailQty, "Mail Qty"])}
          {Object.entries(monthGroups).map(([month, days], i) => (
            <th key={"mmq" + i} colSpan={days.length} className="text-center">
              {monthTotals[month]?.mailQty || 0}
            </th>
          ))}
        </tr>
        <tr>
          <th
            colSpan={3}
            className="bg-primary text-white sticky left-col-1"
            style={{ zIndex: 3 }}
          >
            Week
          </th>
          {Object.entries(weekGroups).map(([week, days], i) => (
            <th
              key={"w" + i}
              colSpan={days.length}
              className="bg-primary text-white"
            >
              {days[0].displayWeek}
            </th>
          ))}
        </tr>
        <tr>
          {cells(["Total Campaign Qty", totalCampaignQty, "Campaign Qty"])}
          {Object.entries(weekGroups).map(([week, days], i) => (
            <th key={"wcq" + i} colSpan={days.length} className="text-center">
              {weekTotals[week]?.campaignQty || 0}
            </th>
          ))}
        </tr>
        <tr>
          {cells(["Total Mail Qty", totalMailQty, "Mail Qty"])}
          {Object.entries(weekGroups).map(([week, days], i) => (
            <th key={"wmq" + i} colSpan={days.length} className="text-center">
              {weekTotals[week]?.mailQty || 0}
            </th>
          ))}
        </tr>
        <tr>
          <th
            colSpan={3}
            className="bg-dark text-white sticky left-col-1"
            style={{ zIndex: 3 }}
          >
            Date
          </th>
          {timeline.map((d, i) => (
            <th
              key={"d" + i}
              className={`bg-dark ${
                d.isSunday ? "bg-light text-dark" : "text-white"
              }`}
            >
              {d.day}
            </th>
          ))}
        </tr>
        <tr>
          {cells(["Total Campaign Qty", totalCampaignQty, "Campaign Qty"])}
          {timeline.map((d, i) => (
            <th
              key={"dcq" + i}
              className={`text-center ${
                d.isSunday ? "bg-light text-dark" : ""
              } ${highlightedCell.col === i ? "bg-success-subtle" : ""}`}
            >
              {" "}
              {dailyTotals[d.key]?.campaignQty || ""}
            </th>
          ))}
        </tr>
        <tr>
          {cells(["Total Mail Qty", totalMailQty, "Mail Qty"])}
          {timeline.map((d, i) => (
            <th
              className={`text-center ${
                d.isSunday ? "bg-light text-dark" : ""
              } ${highlightedCell.col === i ? "bg-success-subtle" : ""}`}
              key={"dmq" + i}
            >
              {" "}
              {dailyTotals[d.key]?.mailQty || ""}
            </th>
          ))}
        </tr>
        <tr>
          <th className="table-secondary text-center sticky left-col-1">
            Brand
          </th>
          <th className="table-secondary text-center sticky left-col-2">
            Product
          </th>
          <th className="table-secondary text-center sticky left-col-3">
            Latest Mail Date
          </th>
          {timeline.map((d, i) => (
            <th
              key={"data" + i}
              className={`text-center ${
                d.isSunday ? "bg-light text-dark" : ""
              } ${highlightedCell.col === i ? "bg-success-subtle" : ""}`}
            >
              {dayjs(d.key).format("dd")}
            </th>
          ))}
        </tr>
      </thead>
    );
  };

  const renderTableBody = () => {
    return (
      <tbody>
        {products.map((product) => {
          const latestMediaPlanDate =
            product.media_plans.length > 0
              ? dayjs(
                  product.media_plans.reduce((latest, curr) =>
                    dayjs(curr.date).isAfter(dayjs(latest.date)) ? curr : latest
                  ).date
                ).format("YYYY-MM-DD")
              : "";

          return (
            <tr key={product.id}>
              <td
                className={`table-light sticky left-col-1 ${
                  highlightedCell.row === product.id ? "bg-success-subtle" : ""
                }`}
              >
                {product.brand}
              </td>

              <td
                className={`table-light sticky left-col-2 ${
                  highlightedCell.row === product.id ? "bg-success-subtle" : ""
                }`}
              >
                {product.product_variation_code}
              </td>

              <td
                className={`table-light sticky left-col-3 ${
                  highlightedCell.row === product.id ? "bg-success-subtle" : ""
                }`}
              >
                {latestMediaPlanDate}
              </td>

              {timeline.map((day, j) => {
                const matchedPlan = product.media_plans.find(
                  (mp) => dayjs(mp.date).format("YYYY-MM-DD") === day.key
                );
                return (
                  <td
                    key={j}
                    className={`text-center ${
                      day.isSunday ? "bg-light text-dark" : ""
                    } ${
                      highlightedCell.row === product.id ||
                      highlightedCell.col === j
                        ? "bg-success-subtle"
                        : ""
                    }`}
                  >
                    {matchedPlan && (
                      <Popover
                        content={
                          <div>
                            <strong>Campaign:</strong>{" "}
                            {matchedPlan.campaign_code}
                            <br />
                            <strong>Qty:</strong> {matchedPlan.target_mail_qty}
                            <br />
                            {setOpenViewTabs && (
                              <button
                                className="btn btn-sm btn-primary mt-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Close the popover
                                  setOpenPopoverId(null);
                                  setHighlightedCell({ row: null, col: null });
                                  
                                  // Open in new tab
                                  const mediaPlanId = matchedPlan.id;
                                  // In the timeline view, client_id might be in a different location
                                  // Try all possible locations where client ID might be stored
                                  const clientId = matchedPlan.client_id || 
                                                 matchedPlan.client?.id || 
                                                 matchedPlan.offer_details?.client?.id || 
                                                 product.client_id || 
                                                 selectedClients[0]; // Fallback to first selected client
                                  const campaignCode = matchedPlan.campaign_code;
                                  
                                  console.log('Opening campaign tab with:', { mediaPlanId, clientId, campaignCode, matchedPlan });
                                  
                                  if (mediaPlanId && clientId) {
                                    const tabKey = `view-${clientId}-${mediaPlanId}`;
                                    
                                    // First add the tab if it doesn't exist
                                    setOpenViewTabs((prev) => {
                                      const alreadyExists = prev.some((tab) => tab.key === tabKey);
                                      if (!alreadyExists) {
                                        return [
                                          ...prev,
                                          {
                                            key: tabKey,
                                            label: `View ${campaignCode || mediaPlanId}`,
                                            content: (
                                              <ViewMediaPlan
                                                mediaPlanId={mediaPlanId}
                                                clientId={clientId}
                                              />
                                            ),
                                            closable: true,
                                          },
                                        ];
                                      }
                                      return prev;
                                    });
                                    
                                    // Then set the active tab - this needs to happen after the tab is added
                                    setTimeout(() => {
                                      if (setForcedTabKey) setForcedTabKey(tabKey);
                                      if (setActiveViewTabKey) setActiveViewTabKey(tabKey);
                                    }, 100);
                                  }
                                }}
                              >
                                View Campaign
                              </button>
                            )}
                          </div>
                        }
                        title="Media Plan Details"
                        trigger="click"
                        placement="right"
                        open={openPopoverId === `${product.id}-${j}`}
                        onOpenChange={(visible) => {
                          if (visible) {
                            setOpenPopoverId(`${product.id}-${j}`);
                            setHighlightedCell({ row: product.id, col: j });
                          } else {
                            setOpenPopoverId(null);
                            setHighlightedCell({ row: null, col: null });
                          }
                        }}
                      >
                        <span style={{ cursor: "pointer" }}>
                          <CheckCircleTwoTone
                            twoToneColor="#52c41a"
                            style={{ fontSize: "16px" }}
                          />
                        </span>
                      </Popover>
                    )}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    );
  };

  return (
    <>
      <Row>
        <Col>
          <DatePicker
            value={startDate}
            allowClear={false}
            onChange={(date) => setStartDate(date.startOf("day"))}
            format="DD-MM-YYYY"
            placeholder="Start Date"
          />
        </Col>
        <Col>
          <span className="p-2">to</span>
        </Col>
        <Col>
          <DatePicker
            value={endDate}
            allowClear={false}
            onChange={(date) => setEndDate(date.startOf("day"))}
            format="DD-MM-YYYY"
            placeholder="End Date"
          />
        </Col>
      </Row>

      <div
        className="table-responsive border rounded small-font"
        style={{ overflowX: "auto" }}
      >
        <table
          className="table table-bordered table-sm mb-0"
          style={{ minWidth: "100%" }}
        >
          {renderTableHeader()}
          {renderTableBody()}
        </table>
      </div>
    </>
  );
};

export default TimelineTable;
